import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// 편집 모드 아래 탭 줄 — 단축키로 이전·다음 탭, 지금 탭이 탭 줄에서 잘 보이게.
// Ctrl+PgUp/PgDn 은 브라우저(Chrome·Edge)가 탭 전환으로 먼저 가져가 페이지에 오지 않으므로 Alt+↑/↓ · Alt+PgUp/PgDn 도 받는다 (구글 시트와 같게).
// 단축키로 옮기면 지금 칸 자리(행·열)를 새 시트에도 놓아 키보드로 이어서 편집하고, 탭 줄은 그 탭을 가운데로 맞춘다.
// 클릭으로 옮기면 탭 줄은 그 탭이 가려졌을 때만 보이게 민다 (누른 탭이 마우스 밑에서 도망가지 않게).

export interface SheetCell { col: number; row: number }

/** 탭 단축키 — 이전 -1, 다음 +1, 아니면 0 */
function stepOf(e: KeyboardEvent) {
  if (e.shiftKey) return 0
  if ((e.key === 'PageDown' || e.key === 'PageUp') && (e.ctrlKey || e.metaKey || e.altKey)) return e.key === 'PageDown' ? 1 : -1
  if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && e.altKey && !e.ctrlKey && !e.metaKey) return e.key === 'ArrowDown' ? 1 : -1
  return 0
}

// 칸 편집 중·이름 바꾸기·서랍 입력칸에서는 탭을 옮기지 않는다 (적던 글자가 사라지지 않게)
const typing = (el: Element | null) =>
  el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el instanceof HTMLElement && el.isContentEditable)

export function useSheetTabs({ keys, activeKey, setActiveKey, blocked, getCell }: {
  keys: string[]
  activeKey: string | null
  setActiveKey: (key: string) => void
  blocked?: () => boolean // 서랍·대화상자가 열려 있으면 true
  getCell?: () => SheetCell | null // 지금 시트의 커서 자리
}) {
  const barRef = useRef<HTMLDivElement>(null)
  const [startCell, setStartCell] = useState<SheetCell | null>(null)
  const align = useRef<'center' | 'nearest'>('center') // 처음 열 때·단축키는 가운데로
  const first = useRef(true)
  const latest = useRef({ keys, activeKey, setActiveKey, blocked, getCell })
  useEffect(() => { latest.current = { keys, activeKey, setActiveKey, blocked, getCell } })

  // 표(document)보다 앞(window capture)에서 가로챈다 — Alt+↑/↓ 가 표의 커서를 움직이지 않게
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const step = stepOf(e)
      if (!step || e.isComposing) return
      const { keys: ks, activeKey: key, setActiveKey: set, blocked: isBlocked, getCell: cell } = latest.current
      if (isBlocked?.() || typing(document.activeElement)) return
      e.preventDefault()
      e.stopPropagation()
      const next = ks[ks.indexOf(key ?? '') + step]
      if (!next) return
      setStartCell(cell?.() ?? null)
      align.current = 'center'
      set(next)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [])

  // 세로 휠로도 탭 줄을 가로로 민다 (스크롤바는 숨김)
  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      e.preventDefault()
      bar.scrollLeft += e.deltaY
    }
    bar.addEventListener('wheel', onWheel, { passive: false })
    return () => bar.removeEventListener('wheel', onWheel)
  }, [])

  // 지금 탭을 탭 줄에서 보이게 — 단축키·처음은 가운데, 클릭은 가려졌을 때만 가장자리에서 조금 띄워
  useLayoutEffect(() => {
    const bar = barRef.current
    const tab = activeKey ? bar?.querySelector<HTMLElement>(`[data-tab="${CSS.escape(activeKey)}"]`) : null
    if (!bar || !tab) return
    const left = tab.offsetLeft
    const right = left + tab.offsetWidth
    const pad = 48
    let target: number
    if (align.current === 'center') target = left - (bar.clientWidth - tab.offsetWidth) / 2
    else if (left < bar.scrollLeft + pad) target = left - pad
    else if (right > bar.scrollLeft + bar.clientWidth - pad) target = right - bar.clientWidth + pad
    else return
    bar.scrollTo({ left: Math.max(0, target), behavior: first.current ? 'auto' : 'smooth' })
    first.current = false
  }, [activeKey, keys.length])

  /** 탭 클릭 */
  const select = (key: string) => {
    align.current = 'nearest'
    setStartCell(null)
    setActiveKey(key)
  }

  return { barRef, startCell, select }
}
